import _ from "lodash"
import {
  z,
  ZodString,
  ZodError,
  ZodOptional,
  ZodObject,
  ZodNullable,
} from "zod"

import { ZodFormObjectTypeDescription } from "@tfg/types/constants/zodFromInputTypes"
import {
  cssColorRegex,
  CSSColorSchema,
} from "@tfg/types/schemas/communication-studio/cssStylesSchema"

export function flattenZodIssuesToNestedObject<T>(
  issues: ZodError["issues"]
): Partial<Record<keyof T, string>> {
  const nestedErrors: Partial<Record<keyof T, string>> = {}

  for (const issue of issues) {
    if (issue.path.length > 0 && issue.message) {
      _.set(nestedErrors, issue.path, issue.message)
    }
  }

  return nestedErrors
}

/**
 * Type guard to check if a value is a ZodOptional or ZodNullable
 */
function isZodOptionalOrNullable(
  field: z.ZodTypeAny
): field is ZodOptional<z.ZodTypeAny> | ZodNullable<z.ZodTypeAny> {
  return field instanceof ZodOptional || field instanceof ZodNullable
}

/**
 * Unwraps ZodOptional or ZodNullable to get the inner type.
 * The innerType from Zod v4's internal structure ($ZodType) is compatible
 * with ZodTypeAny at runtime, verified by instanceof checks.
 * We use a type assertion here because TypeScript's type system doesn't
 * recognize the runtime compatibility between $ZodType and ZodTypeAny,
 * but this is safe due to the instanceof guard above.
 */
function unwrapZodOptionalOrNullable(field: z.ZodTypeAny): z.ZodTypeAny {
  if (isZodOptionalOrNullable(field)) {
    // After instanceof check, we know innerType is a valid Zod type
    // The assertion is necessary due to Zod v4's type system limitations
    // but is safe because we've verified the type at runtime
    const innerType = field._zod.def.innerType
    // Verify at runtime that innerType has Zod-like properties
    if (
      typeof innerType === "object" &&
      innerType !== null &&
      ("_zod" in innerType || "parse" in innerType || "safeParse" in innerType)
    ) {
      return innerType as z.ZodTypeAny
    }
    throw new Error("Invalid innerType from ZodOptional/ZodNullable")
  }
  return field
}

/**
 * Type guard to check if a value is a Zod type (either ZodTypeAny or $ZodType).
 * This handles Zod v4's internal type structure where innerType returns $ZodType.
 */
function isZodType(value: unknown): value is z.ZodTypeAny {
  return (
    typeof value === "object" &&
    value !== null &&
    ("_zod" in value || "parse" in value || "safeParse" in value)
  )
}

const getZodObjectTypeFromDescription = (
  field: z.ZodTypeAny
): [string, z.ZodTypeAny] | undefined => {
  const finalField = unwrapZodOptionalOrNullable(field)

  if (finalField instanceof ZodObject && finalField.description) {
    switch (finalField.description) {
      case ZodFormObjectTypeDescription.ZOD_FILE:
        return [ZodFormObjectTypeDescription.ZOD_FILE, field]
      case ZodFormObjectTypeDescription.ZOD_STYLING_CLASS:
        return [ZodFormObjectTypeDescription.ZOD_STYLING_CLASS, field]
      case ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE:
        return [ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE, field]
      default:
        return undefined
    }
  }
  return undefined
}

export const getZodFieldType = (
  shape: z.ZodRawShape,
  key: string
): [string, z.ZodTypeAny] => {
  const field = shape[key]
  if (!field) {
    throw new Error(`Field ${key} not found in schema shape`)
  }

  // Ensure field is ZodTypeAny (it should be from ZodRawShape, but TypeScript needs help)
  if (!isZodType(field)) {
    throw new Error(`Field ${key} is not a valid Zod type`)
  }

  const describedType = getZodObjectTypeFromDescription(field)
  if (describedType) {
    return describedType
  }

  const unwrappedField = unwrapZodOptionalOrNullable(field)

  // Handle nested optional/nullable
  if (isZodOptionalOrNullable(unwrappedField)) {
    const innerType = unwrapZodOptionalOrNullable(unwrappedField)
    return [innerType._zod.def.type, innerType]
  }

  return [unwrappedField._zod.def.type, unwrappedField]
}

export const getKeyFromPath = (path: string[]): string => {
  return path[path.length - 1]
}

export function getClassPropsShapeAndKey(
  field: z.ZodTypeAny
): [z.ZodRawShape, string[]] {
  const finalField = unwrapZodOptionalOrNullable(field)

  if (!(finalField instanceof ZodObject)) {
    throw new Error("Expected ZodObject")
  }

  const propsField = finalField.shape.props
  if (!(propsField instanceof ZodObject)) {
    throw new Error("Expected props to be ZodObject")
  }

  const classPropsShape = propsField.shape
  const classPropsKeys = Object.keys(classPropsShape)
  return [classPropsShape, classPropsKeys]
}

export function getCssVariableShape(field: z.ZodTypeAny): z.ZodTypeAny {
  const finalField = unwrapZodOptionalOrNullable(field)

  if (!(finalField instanceof ZodObject)) {
    throw new Error("Expected ZodObject")
  }

  const cssFieldShape = finalField.shape.value
  return unwrapZodOptionalOrNullable(cssFieldShape)
}

interface ZodCheckRegex {
  _zod: {
    def: {
      format: "regex"
      pattern: RegExp
    }
  }
}

function isZodCheckRegex(check: unknown): check is ZodCheckRegex {
  if (typeof check !== "object" || check === null) return false

  const zodProp = (check as { _zod?: unknown })._zod
  if (typeof zodProp !== "object" || zodProp === null) return false

  const defProp = (zodProp as { def?: unknown }).def
  if (typeof defProp !== "object" || defProp === null) return false

  const def = defProp as { format?: unknown; pattern?: unknown }
  return def.format === "regex" && def.pattern instanceof RegExp
}

export function isCSSColorSchema(
  schema: z.ZodTypeAny
): schema is typeof CSSColorSchema {
  if (!(schema instanceof ZodString)) return false

  const checks = schema._zod.def.checks ?? []
  return checks.some((check) => {
    if (!isZodCheckRegex(check)) return false
    return check._zod.def.pattern.source === cssColorRegex.source
  })
}

export function parseZodErrors<T>(
  errors: Record<string, string[] | undefined> | undefined
): Partial<Record<keyof T, string>> {
  if (!errors) return {}
  return Object.entries(errors).reduce(
    (acc, [key, val]) => {
      if (val?.length) acc[key as keyof T] = val[0]
      return acc
    },
    {} as Partial<Record<keyof T, string>>
  )
}

function shouldKeep<TOriginal>(
  value: TOriginal | undefined | (object & Record<string, never>)
): value is Exclude<TOriginal, undefined | (object & Record<string, never>)> {
  if (_.isUndefined(value)) {
    return false
  }
  if (_.isPlainObject(value) && _.isEmpty(value)) {
    return false
  }
  return true
}

type Cleaned<T> = T extends object
  ? { [K in keyof T]?: Cleaned<T[K]> } | undefined
  : T extends (infer U)[]
    ? Cleaned<U>[] | undefined
    : T | undefined

export function cleanArray<T>(array: T[]): Cleaned<T>[] | undefined {
  const cleanedItems = array.map((item) => cleanEmptyObjects(item))

  if (cleanedItems.length === 0) return undefined
  return cleanedItems as Cleaned<T>[]
}

function cleanObject<T extends Record<string, unknown>>(
  obj: T
): Cleaned<T> | undefined {
  const cleanedResult: Record<string, unknown> = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      const cleanedValue = cleanEmptyObjects(value)

      if (shouldKeep(cleanedValue)) {
        cleanedResult[key] = cleanedValue
      }
    }
  }

  if (_.isEmpty(cleanedResult)) {
    return undefined
  }

  return cleanedResult as Cleaned<T>
}

export function cleanEmptyObjects<T>(obj: T[]): Cleaned<T>[] | undefined
export function cleanEmptyObjects<T extends object>(
  obj: T
): Cleaned<T> | undefined
export function cleanEmptyObjects<T>(obj: T): Cleaned<T> | undefined
export function cleanEmptyObjects(obj: unknown): unknown {
  if (!_.isObject(obj) || obj === null) {
    return obj
  }

  if (_.isArray(obj)) {
    return cleanArray(obj)
  }

  return cleanObject(obj as Record<string, unknown>)
}
